export type AppearanceImage = {
  id: string;
  name: string;
  colors: string[];
  textureImage?: string;
};

export const toAppearanceImage = (appearance: ProductTypeAppearance): AppearanceImage => ({
  id: appearance.id,
  name: appearance.name,
  colors: appearance.colors,
  textureImage: appearance.texture ? appearance.resources[0]?.href : undefined,
});

/** Same as toAppearanceImage, but for the public-API appearance shape. */
export const toAppearanceImageFromAppearance = (appearance: Appearance): AppearanceImage => ({
  id: appearance.id,
  name: appearance.name,
  colors: [...appearance.colors].sort((a, b) => a.index - b.index).map((color) => color.value),
  textureImage: appearance.texture ? appearance.resources?.[0]?.href : undefined,
});

/**
 * Distinguishes an already trimmed AppearanceImage from a full public-API
 * Appearance (e.g. recently-viewed localStorage entries written before the
 * trimmed shape was introduced).
 */
export const isAppearanceImage = (
  appearance: AppearanceImage | Appearance
): appearance is AppearanceImage => !('texture' in appearance);

/**
 * Deduplicates a flat list of product-type appearances into a map keyed by
 * appearance id. Appearance metadata is stable per id across product types, so
 * the same id always resolves to the same colors/texture. Serializing this map
 * once (via AppearanceConfigProvider) avoids repeating the full appearance
 * objects in the RSC payload for every product card on a list page.
 */
export const buildAppearanceConfig = (
  appearances: ProductTypeAppearance[]
): Record<string, AppearanceImage> => {
  const config: Record<string, AppearanceImage> = {};

  for (const appearance of appearances) {
    if (!config[appearance.id]) {
      config[appearance.id] = toAppearanceImage(appearance);
    }
  }

  return config;
};

/**
 * Like buildAppearanceConfig, but for appearances that have already been mapped
 * into AppearanceImage shape (e.g. the product-slider modules, whose upstream
 * appearance colors differ from ProductTypeAppearance). Deduplicates by id,
 * keeping the first occurrence, so the shared map is serialized once instead of
 * repeating the full appearance objects on every card.
 */
export const buildAppearanceConfigFromImages = (
  appearances: AppearanceImage[]
): Record<string, AppearanceImage> => {
  const config: Record<string, AppearanceImage> = {};

  for (const appearance of appearances) {
    if (!config[appearance.id]) {
      config[appearance.id] = appearance;
    }
  }

  return config;
};

/** Same as buildAppearanceConfig, but for the public-API appearance shape. */
export const buildAppearanceConfigFromApi = (
  appearances: Appearance[]
): Record<string, AppearanceImage> => {
  const config: Record<string, AppearanceImage> = {};

  for (const appearance of appearances) {
    if (!config[appearance.id]) {
      config[appearance.id] = toAppearanceImageFromAppearance(appearance);
    }
  }

  return config;
};

/**
 * Trims a product type for client-component props: appearances are reduced to
 * their ids, the full appearance data is resolved via AppearanceConfigProvider.
 */
export const toClientProductType = <T extends ProductType>(
  productType: T
): ClientProductType<T> => {
  const { appearances, ...rest } = productType;
  return { ...rest, appearanceIds: appearances.map((appearance) => appearance.id) };
};

/**
 * Replaces the inline `appearances` of already-mapped product card items with
 * `appearanceIds` and collects the appearance data into a single deduplicated
 * config. Serializing the config once (via AppearanceConfigProvider) instead
 * of the full appearance list per card keeps dehydrated recommendation
 * payloads small.
 */
export const extractAppearanceConfig = <T extends { appearances?: AppearanceImage[] }>(
  items: T[]
): {
  items: (Omit<T, 'appearances'> & { appearanceIds: string[] })[];
  appearanceConfig: Record<string, AppearanceImage>;
} => {
  const appearanceConfig: Record<string, AppearanceImage> = {};

  const slimItems = items.map(({ appearances, ...rest }) => {
    for (const appearance of appearances ?? []) {
      if (!appearanceConfig[appearance.id]) {
        appearanceConfig[appearance.id] = appearance;
      }
    }
    return { ...rest, appearanceIds: (appearances ?? []).map(({ id }) => id) };
  });

  return { items: slimItems, appearanceConfig };
};
